import React, { useState } from 'react';
import { SearchIcon } from '../icons/SearchIcon';

interface SearchFiltersProps {
  filters: {
    limit: number;
    messageId: string | undefined;
  };
  onFilterChange: (filters: Partial<SearchFiltersProps['filters']>) => void;
  onNewSearch: (query: string) => void;
  currentQuery: string;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  filters,
  onFilterChange,
  onNewSearch,
  currentQuery
}) => {
  const [newQuery, setNewQuery] = useState('');

  const handleNewSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (newQuery.trim()) {
      onNewSearch(newQuery.trim());
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ limit: parseInt(e.target.value) });
  };

  const handleMessageIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ messageId: e.target.value || undefined });
  };

  const clearMessageId = () => {
    onFilterChange({ messageId: undefined });
  };

  return (
    <div className="flex items-center gap-6">
      {/* New Search */}
      <div className="flex-1">
        <form onSubmit={handleNewSearch}>
          <div className="relative">
            <input
              type="text"
              value={newQuery}
              onChange={(e) => setNewQuery(e.target.value)}
              placeholder="Search chats..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
              <SearchIcon />
            </button>
          </div>
        </form>
      </div>

      {/* Current Query Display */}
      {currentQuery && (
        <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
          <p className="text-blue-800 font-medium text-sm">"{currentQuery}"</p>
        </div>
      )}

      {/* Results Limit */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700">Results:</label>
        <select
          value={filters.limit}
          onChange={handleLimitChange}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Message ID Filter */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700">Message ID:</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={filters.messageId || ''}
            onChange={handleMessageIdChange}
            placeholder="Optional..."
            className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {filters.messageId && (
            <button
              onClick={clearMessageId}
              className="px-2 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onNewSearch(currentQuery)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
        >
          Refresh
        </button>
        <button
          onClick={() => onFilterChange({ limit: 20, messageId: undefined })}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default SearchFilters;
